<template>
  <v-dialog v-model="isOpen" width="800">
    <v-card>
      <v-card-title>
        {{ vorstoss.title }}
      </v-card-title>
      <v-card-text>
        <dl>
          <dt>{{ $t('Status') }}</dt>
          <dd>{{ vorstoss.status_display }}</dd>

          <dt>{{ $t('Business_type') }}</dt>
          <dd>{{ businessTypeLabel }}</dd>

          <dt>{{ $t('Wording') }}</dt>
          <dd>{{ vorstoss.description }}</dd>

          <dt>{{ $t('Business_case_reason') }}</dt>
          <dd>{{ vorstoss.reason }}</dd>

          <dt>{{ $t('Business_case_proposal_creator') }}</dt>
          <dd>
            <VorstossSignatory :signatory="vorstoss.first_signatory" />
          </dd>

          <dt>{{ $t('Business_case_second_signature') }}</dt>
          <dd>
            <VorstossSignatory :signatory="vorstoss.second_signatory" />
          </dd>

          <dt>{{ $t('Business_case_third_signature') }}</dt>
          <dd>
            <VorstossSignatory :signatory="vorstoss.third_signatory" />
          </dd>

          <dt>{{ $t('Business_case_other_signature') }}</dt>
          <dd v-if="otherSignatories">
            <v-row>
              <v-col col="11">
                <VorstossSignatory
                  v-for="(signatory, index) in otherSignatories"
                  :key="`other-signatory-${signatory.type}-${signatory.id}`"
                  :signatory="signatory"
                  class="d-inline-flex align-center mr-2"
                  hide-icon
                >
                  <template #append>
                    <span v-if="index < otherSignatories.length - 1">,</span>
                  </template>
                </VorstossSignatory>
              </v-col>
              <v-col cols="auto">
                <v-btn color="green" small rounded @click="isShowAllMembers = !isShowAllMembers">
                  <v-icon color="white" size="16">{{ isShowAllMembers ? 'mdi-eye' : 'mdi-eye-off' }}</v-icon>
                  <span class="ml-2 white--text">{{
                    isShowAllMembers ? $t('vorstossHideCoSignerMembers') : $t('vorstossShowCoSignerMembers')
                  }}</span>
                </v-btn>
              </v-col>
            </v-row>
            <VExpandTransition>
              <template v-if="isShowAllMembers">
                <v-card outlined class="mt-4">
                  <v-card-text class="member-card">
                    <div
                      v-for="(signatory, index) in getOtherSignatoriesMembers"
                      :key="`signatory-${signatory.type}-${signatory.id}`"
                    >
                      <template v-if="signatory.type === 'user'">
                        <VorstossSignatory :signatory="signatory" />
                      </template>
                      <template v-else>
                        <VorstossSignatory :signatory="signatory" hide-icon :class="index === 0 ? 'mb-2' : 'my-2'" />
                        <VorstossSignatory
                          v-for="(member, member_index) in signatory.members"
                          :key="`member-${member}-${member_index}`"
                          :class="{ 'mb-2': member_index === signatory.members.length - 1 }"
                          :signatory="member"
                          :has-signed="signedMembers.has(member)"
                          list
                        >
                          <template #item="{ item }">
                            {{ item }}
                          </template>
                        </VorstossSignatory>
                      </template>
                    </div>
                  </v-card-text>
                </v-card>
              </template>
            </VExpandTransition>
          </dd>
          <dd v-else>-</dd>

          <dt>{{ $t('Business_case_rights_holder') }}</dt>
          <dd>{{ vorstoss.rights_holder_display }}</dd>

          <dt>{{ $t('mark_urgent') }}</dt>
          <dd>
            <v-icon small :color="enabledColor(vorstoss.is_urgent)">{{ enabledIcon(vorstoss.is_urgent) }}</v-icon>
          </dd>

          <dt>{{ $t('Attachments') }}</dt>
          <dd>
            <ul class="pl-4">
              <li v-for="attachment in vorstoss.attachments" :key="attachment.name">
                <a :href="attachment.url">{{ attachment.name }}</a>
              </li>
            </ul>
          </dd>
        </dl>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn text @click="isOpen = false">{{ $t('Cancel') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import get from 'lodash/get'
import isUndefined from 'lodash/isUndefined'

export default {
  name: 'VorstossDetailDialog',
  data() {
    return {
      isOpen: false,
      vorstoss: {},
      isShowAllMembers: false,
    }
  },
  computed: {
    otherSignatories() {
      return this.getUniqueSignatories(this.vorstoss.other_signatories || [])
    },
    getOtherSignatoriesMembers() {
      return this.getMembers(this.vorstoss.other_signatories || [])
    },
    businessTypeLabel() {
      return get(this.vorstoss, 'business_type.text')
    },
    signedMembers() {
      return new Set(this.vorstoss.other_signatories.map((signatory) => signatory.signed_by).filter((id) => id != null))
    },
  },
  methods: {
    getMembers(list) {
      const signatories = new Map()
      list.forEach((item) => {
        const name = this.signatoryLabel(item)
        if (!signatories.has(name)) {
          signatories.set(name, item)
        }
      })
      return Array.from(signatories.values())
    },
    getUniqueSignatories(list) {
      const signatories = new Map()
      list.forEach((item) => {
        const name = this.signatoryLabel(item)
        const existing = signatories.get(name)
        if (!existing || (!existing.is_signed && item.is_signed)) {
          signatories.set(name, item)
        }
      })
      return Array.from(signatories.values())
    },
    open(vorstoss) {
      this.vorstoss = vorstoss
      this.isOpen = true
    },
    signatoryLabel(signatory) {
      if (isUndefined(signatory)) return ''
      if (signatory.type === 'user') return signatory.full_name
      if (signatory.type === 'committee' || signatory.type === 'committeegroup') return signatory.title || ''
      return ''
    },
    enabledIcon(value) {
      return value ? 'mdi-check-circle' : 'mdi-close-circle'
    },
    enabledColor(value) {
      return value ? 'success' : 'error'
    },
  },
}
</script>
<style scoped>
.member-card {
  max-height: 200px;
  overflow-y: auto;
}
</style>
